import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { useDiagramStore, useSelectionStore, useViewportStore } from '@/stores'
import { getTableBounds, rectsIntersect } from '@/utils/geometry'

interface ScreenRect {
  left: number
  top: number
  width: number
  height: number
}

interface SelectionDragState {
  pointerId: number
  startClientX: number
  startClientY: number
  currentClientX: number
  currentClientY: number
  containerLeft: number
  containerTop: number
}

const DRAG_THRESHOLD_PX = 5

const IGNORE_SELECTORS = ['.etb', '[data-action]', '#legend', '#rel-legend']
const IGNORE_TAGS = new Set(['path', 'circle'])
const IGNORE_CLASSES = ['rel-handle', 'wp-handle', 'wp-add-hint']

function shouldIgnoreTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (IGNORE_SELECTORS.some((selector) => target.closest(selector))) return true
  if (IGNORE_CLASSES.some((className) => target.classList.contains(className))) return true
  return IGNORE_TAGS.has(target.tagName.toLowerCase())
}

function buildScreenRect(
  startClientX: number,
  startClientY: number,
  currentClientX: number,
  currentClientY: number,
  containerLeft: number,
  containerTop: number,
): ScreenRect {
  const startX = startClientX - containerLeft
  const startY = startClientY - containerTop
  const currentX = currentClientX - containerLeft
  const currentY = currentClientY - containerTop

  return {
    left: Math.min(startX, currentX),
    top: Math.min(startY, currentY),
    width: Math.abs(currentX - startX),
    height: Math.abs(currentY - startY),
  }
}

export function useSelectionRect(containerRef: RefObject<HTMLDivElement>) {
  const dragStateRef = useRef<SelectionDragState | null>(null)
  const containerElementRef = useRef<HTMLDivElement | null>(null)
  const suppressClickRef = useRef(false)
  const restoreBodyUserSelectRef = useRef('')
  const [selectionRect, setSelectionRect] = useState<ScreenRect | null>(null)

  const cleanup = useCallback(() => {
    const containerElement = containerElementRef.current
    const dragState = dragStateRef.current

    if (containerElement && dragState) {
      try {
        if (containerElement.hasPointerCapture(dragState.pointerId)) {
          containerElement.releasePointerCapture(dragState.pointerId)
        }
      } catch {
        // no-op
      }
    }

    window.removeEventListener('pointermove', handlePointerMove)
    window.removeEventListener('pointerup', handlePointerUp)
    window.removeEventListener('pointercancel', handlePointerCancel)

    document.body.style.userSelect = restoreBodyUserSelectRef.current

    dragStateRef.current = null
    containerElementRef.current = null
    setSelectionRect(null)
  }, [])

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current
    if (!dragState || event.pointerId !== dragState.pointerId) return

    event.preventDefault()

    dragState.currentClientX = event.clientX
    dragState.currentClientY = event.clientY

    setSelectionRect(
      buildScreenRect(
        dragState.startClientX,
        dragState.startClientY,
        dragState.currentClientX,
        dragState.currentClientY,
        dragState.containerLeft,
        dragState.containerTop,
      ),
    )
  }, [])

  const handlePointerUp = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current
    if (!dragState || event.pointerId !== dragState.pointerId) return

    const rect = buildScreenRect(
      dragState.startClientX,
      dragState.startClientY,
      dragState.currentClientX,
      dragState.currentClientY,
      dragState.containerLeft,
      dragState.containerTop,
    )

    suppressClickRef.current = true
    cleanup()

    if (rect.width < DRAG_THRESHOLD_PX && rect.height < DRAG_THRESHOLD_PX) {
      useSelectionStore.getState().clearSelection()
      return
    }

    const viewport = useViewportStore.getState()
    const topLeft = viewport.clientToCanvas(
      dragState.containerLeft + rect.left,
      dragState.containerTop + rect.top,
      { left: dragState.containerLeft, top: dragState.containerTop },
    )
    const bottomRight = viewport.clientToCanvas(
      dragState.containerLeft + rect.left + rect.width,
      dragState.containerTop + rect.top + rect.height,
      { left: dragState.containerLeft, top: dragState.containerTop },
    )

    const canvasRect = {
      x: Math.min(topLeft.x, bottomRight.x),
      y: Math.min(topLeft.y, bottomRight.y),
      w: Math.abs(bottomRight.x - topLeft.x),
      h: Math.abs(bottomRight.y - topLeft.y),
    }

    const selectedIds = useDiagramStore
      .getState()
      .tables
      .filter((table) => rectsIntersect(getTableBounds(table), canvasRect))
      .map((table) => table.id)

    useSelectionStore.getState().selectMultipleTables(selectedIds)
  }, [cleanup])

  const handlePointerCancel = useCallback((event: PointerEvent) => {
    const dragState = dragStateRef.current
    if (!dragState || event.pointerId !== dragState.pointerId) return

    cleanup()
  }, [cleanup])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handlePointerDown = (event: PointerEvent) => {
      const { canvasMode, relMode } = useSelectionStore.getState()

      if (relMode) return
      if (canvasMode !== 'select') return
      if (event.button !== 0) return
      if (shouldIgnoreTarget(event.target)) return

      const rect = container.getBoundingClientRect()

      event.preventDefault()

      restoreBodyUserSelectRef.current = document.body.style.userSelect
      document.body.style.userSelect = 'none'

      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        currentClientX: event.clientX,
        currentClientY: event.clientY,
        containerLeft: rect.left,
        containerTop: rect.top,
      }
      containerElementRef.current = container
      suppressClickRef.current = false

      container.setPointerCapture(event.pointerId)
      setSelectionRect({
        left: event.clientX - rect.left,
        top: event.clientY - rect.top,
        width: 0,
        height: 0,
      })

      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
      window.addEventListener('pointercancel', handlePointerCancel)
    }

    container.addEventListener('pointerdown', handlePointerDown)

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown)
      cleanup()
    }
  }, [cleanup, containerRef, handlePointerCancel, handlePointerMove, handlePointerUp])

  const handleCanvasClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return

    suppressClickRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }, [])

  return {
    selectionRect,
    handleCanvasClickCapture,
  }
}
